import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import reportJobsApi, { type ReportJobI } from 'Api/axios/report-jobs-api';
import { REPORT_TOAST_OPTIONS } from 'Components/ReportJobsWidget/toast-options';
import { downloadFile } from 'Utils/file-utils';
import { useReportJobsStore, isJobActive } from 'stores/report-jobs-store';

/**
 * Cada cuánto se le pregunta al servidor por el avance. Dos segundos dan una
 * barra que se mueve de verdad; la respuesta es un JSON diminuto y mientras no
 * haya reportes corriendo no se consulta nada.
 */
const POLL_INTERVAL_MS = 2000;

/**
 * Pausa entre descargas encadenadas.
 *
 * Los navegadores bloquean las descargas automáticas cuando varias salen a la
 * vez: permiten la primera y piden permiso para el resto, y si no se concede,
 * las siguientes fallan sin decir nada. Separarlas evita disparar esa defensa.
 */
const DOWNLOAD_GAP_MS = 900;

const wait = (ms: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Sigue los reportes que se están generando en segundo plano y baja el archivo
 * en cuanto está listo.
 *
 * Pensado para usarse en un solo lugar (la ventana de progreso, montada una
 * vez en toda la aplicación). Si se usara en dos, se duplicarían las consultas.
 */
export const useReportJobs = (): {
  refresh: () => Promise<void>;
  download: (job: ReportJobI) => Promise<void>;
} => {
  const setJobs = useReportJobsStore(state => state.setJobs);
  const markHandled = useReportJobsStore(state => state.markHandled);
  const hasActiveJobs = useReportJobsStore(state => state.jobs.some(isJobActive));

  /**
   * Falso hasta que llega la primera respuesta del servidor. Distingue los
   * reportes que ya estaban terminados al abrir la pantalla de los que
   * terminan mientras el usuario mira.
   */
  const hasLoadedOnce = useRef(false);

  /** Cola para que las descargas salgan de una en una. */
  const downloadChain = useRef<Promise<void>>(Promise.resolve());

  const download = useCallback(
    async (job: ReportJobI) => {
      const { data, error } = await reportJobsApi.downloadCoursesReportJob(job.id);

      if (error || !data) {
        toast.error(
          'El reporte se generó, pero no se pudo descargar. Inténtalo desde la ventana de progreso.',
          REPORT_TOAST_OPTIONS
        );
        return;
      }

      downloadFile(data, job.fileName);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /** Encadena la descarga detrás de las anteriores, con una pausa entre cada una. */
  const enqueueDownload = useCallback(
    (job: ReportJobI) => {
      downloadChain.current = downloadChain.current
        .then(() => download(job))
        .then(() => wait(DOWNLOAD_GAP_MS))
        // Que una descarga fallida no rompa la cola de las siguientes.
        .catch(() => undefined);
    },
    [download]
  );

  /**
   * Atiende una sola vez el desenlace de cada reporte. Sin este registro, cada
   * consulta volvería a descargar el mismo archivo mientras siga en la lista.
   */
  const handleFinished = useCallback(
    (jobs: ReportJobI[]) => {
      const { handledIds } = useReportJobsStore.getState();
      const isFirstLoad = !hasLoadedOnce.current;
      hasLoadedOnce.current = true;

      jobs.forEach(job => {
        if (isJobActive(job) || handledIds.includes(job.id)) return;

        markHandled(job.id);

        // Al abrir o recargar la pantalla solo tomamos nota de los reportes que
        // ya habían terminado: bajarlos otra vez sería descargar de golpe todo
        // lo de la última media hora, sin que nadie lo haya pedido. Quedan a un
        // clic en la ventana de progreso.
        if (isFirstLoad) return;

        // Sin aviso de éxito: la ventana de progreso ya muestra la palomita y
        // el archivo empieza a bajar solo. Un toast encima sería ruido, y
        // además taparía esa misma ventana.
        if (job.status === 'done') {
          enqueueDownload(job);
          return;
        }

        toast.error(job.errorMessage ?? 'No se pudo generar el reporte.', REPORT_TOAST_OPTIONS);
      });
    },
    [enqueueDownload, markHandled]
  );

  const refresh = useCallback(async () => {
    // La ventana vive en toda la aplicación, incluida la pantalla de acceso.
    // Sin sesión no hay nada que consultar.
    if (!localStorage.getItem('token')) return;

    const { data } = await reportJobsApi.getCoursesReportJobs();
    if (!data) return;

    setJobs(data);
    handleFinished(data);
  }, [setJobs, handleFinished]);

  // Al montar: recupera los reportes que siguieran corriendo. Es lo que hace
  // que la ventana sobreviva a una recarga de página o a cambiar de pantalla.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // El sondeo solo existe mientras haya algo corriendo, y se apaga solo.
  useEffect(() => {
    if (!hasActiveJobs) return undefined;

    // Consulta ya, sin esperar al primer intervalo: al encolar un reporte se
    // pinta en 0% de forma optimista, y sin esta llamada la barra se quedaría
    // en cero hasta el siguiente ciclo aunque el servidor ya hubiera avanzado.
    refresh();

    const intervalId = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [hasActiveJobs, refresh]);

  return { refresh, download };
};

export default useReportJobs;
